
import React from 'react';
import { cn } from '@/lib/utils';
import { Task, getPriorityLabel } from '@/types/tasks';
import GlassPill from '@/components/ui/GlassPill';
import { Clock } from 'lucide-react';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface TaskCardBadgesProps {
  task: Task;
  isCompleted: boolean;
}

const TaskCardBadges = ({ task, isCompleted }: TaskCardBadgesProps) => {
  const { isMobile } = useIsMobile();
  
  // Check if task is overdue (due date is in the past and task is not completed)
  // This hook must be called before any early returns
  const isOverdue = React.useMemo(() => {
    if (isCompleted || !task.dueDate) return false;
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    // Reset time part to compare just the dates
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  }, [task.dueDate, isCompleted]);
  
  // Hide all badges on mobile
  if (isMobile) {
    return null;
  }
  
  // Map task priority to GlassPill variant
  const getPriorityVariant = () => {
    switch (task.priority) {
      case 'high': 
        return 'red'; // Changed from purple to red for "Critical"
      case 'medium': 
        return 'amber';
      case 'not-important': 
        return 'green';
      default: 
        return 'blue';
    }
  };

  return (
    <>
      {/* Overdue badge */}
      {isOverdue && (
        <GlassPill
          type="Overdue"
          variant="red"
          icon={<Clock size={12} className="text-red-500" />}
          iconPosition="left"
          className={cn(
            "text-xs py-0.5 px-2"
          )}
        />
      )}

      {/* Importance badge */}
      {task.priority && (
        <GlassPill
          type={getPriorityLabel(task.priority)}
          variant={getPriorityVariant()}
          className={cn(
            "text-xs py-0.5 px-2", 
            isCompleted ? "opacity-50" : "opacity-100"
          )}
        />
      )}
    </>
  );
};

export default TaskCardBadges;
